import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import {
  legalDashboardService,
  type LegalDashboardData,
  type AttentionSeverity,
} from '@/services/legal-dashboard.service';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

const ATTENTION_STYLES: Record<AttentionSeverity, string> = {
  high: 'text-danger-600 dark:text-danger-400',
  medium: 'text-warning-600 dark:text-warning-400',
  low: 'text-secondary-600 dark:text-secondary-400',
};

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

interface HeadlineCard {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  color: string;
}

function StatCard({ title, value, icon: Icon, href, color }: HeadlineCard) {
  return (
    <Link to={href} aria-label={`${title}: ${value}`}>
      <Card hover="default" className="transition-all duration-200 h-full">
        <CardContent className="p-5 sm:p-6 h-full">
          <p className="text-sm font-medium leading-tight text-secondary-600 dark:text-secondary-400">
            {title}
          </p>
          <div className="flex items-center justify-between gap-3 mt-3">
            <p className="text-3xl font-bold text-secondary-900 dark:text-white">
              {formatNumber(value)}
            </p>
            <div className={cn('p-3 rounded-lg flex-shrink-0 text-white', color)}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function LegalDashboard() {
  const [data, setData] = useState<LegalDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await legalDashboardService.getDashboard();
        if (active) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        console.error('Failed to load L&C dashboard:', err instanceof Error ? err.message : err);
        if (active) {setError('Failed to load dashboard data');}
      } finally {
        if (active) {setLoading(false);}
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-secondary-600 dark:text-secondary-400">
          Loading Legal &amp; Compliance dashboard...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <div className="text-center text-danger-600">{error ?? 'Failed to load dashboard data'}</div>
      </div>
    );
  }

  const headlineCards: HeadlineCard[] = [
    { title: 'Vendors', value: data.headline.vendors, icon: BuildingOffice2Icon, href: '/legal/vendors', color: 'bg-primary-500' },
    { title: 'Contracts', value: data.headline.contracts, icon: DocumentTextIcon, href: '/legal/contracts', color: 'bg-success-500' },
    { title: 'Open Legal Matters', value: data.headline.matters, icon: ScaleIcon, href: '/legal/matters', color: 'bg-primary-500' },
    { title: 'Open HIPAA Incidents', value: data.headline.hipaaIncidents, icon: ShieldExclamationIcon, href: '/legal/hipaa', color: 'bg-warning-500' },
    { title: 'Open Tracking Requests', value: data.headline.trackingRequests, icon: ClipboardDocumentCheckIcon, href: '/legal/tracking', color: 'bg-primary-500' },
    { title: 'Business Entities', value: data.headline.businessEntities, icon: BuildingOffice2Icon, href: '/legal/hipaa/business-entities', color: 'bg-secondary-500' },
  ];

  const hasActivity = data.activity.length > 0;

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-4 sm:space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Legal &amp; Compliance Dashboard
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              At-a-glance overview of vendors, matters, compliance, and HIPAA incidents
            </p>
          </div>
          <Link to="/legal/analytics">
            <Button variant="outline">
              <ChartBarIcon className="w-4 h-4 mr-2" />
              View Analytics
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Partial-failure warning (non-blocking) */}
      {data.failedSources.length > 0 && (
        <motion.div variants={ITEM_VARIANTS}>
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-900/40 dark:bg-warning-900/20 p-3 text-sm text-warning-800 dark:text-warning-200"
          >
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
            <span>Some data couldn&apos;t be loaded and is shown as zero. Refresh to try again.</span>
          </div>
        </motion.div>
      )}

      {/* Headline counts */}
      <motion.div
        variants={ITEM_VARIANTS}
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6"
      >
        {headlineCards.map((card) => (
          <motion.div key={card.title} variants={ITEM_VARIANTS}>
            <StatCard {...card} />
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Needs attention */}
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardHeader>
              <CardTitle>Needs Attention</CardTitle>
              <CardDescription>Overdue, due-soon, and open high-priority items</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.attention.map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors group"
                  >
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">
                      {item.label}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className={cn('text-lg font-bold', ATTENTION_STYLES[item.severity])}>
                        {formatNumber(item.count)}
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-secondary-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent activity */}
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest legal matters and HIPAA incidents</CardDescription>
            </CardHeader>
            <CardContent>
              {hasActivity ? (
                <div className="space-y-2 max-h-[420px] overflow-y-auto">
                  {data.activity.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-secondary-200 dark:border-secondary-700"
                    >
                      <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {item.title}
                        </h4>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1 flex items-center">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <ScaleIcon className="w-10 h-10 text-secondary-400 dark:text-secondary-600 mb-3" />
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    No recent Legal &amp; Compliance activity.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick actions */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common Legal &amp; Compliance tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Link to="/legal/vendors/create">
                <Button className="w-full justify-start" variant="outline">
                  <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                  New Vendor
                </Button>
              </Link>
              <Link to="/legal/matters/create">
                <Button className="w-full justify-start" variant="outline">
                  <ScaleIcon className="w-4 h-4 mr-2" />
                  New Legal Matter
                </Button>
              </Link>
              <Link to="/legal/hipaa/create">
                <Button className="w-full justify-start" variant="outline">
                  <ShieldExclamationIcon className="w-4 h-4 mr-2" />
                  Report HIPAA Incident
                </Button>
              </Link>
              <Link to="/legal/tracking/create">
                <Button className="w-full justify-start" variant="outline">
                  <ClipboardDocumentCheckIcon className="w-4 h-4 mr-2" />
                  New Tracking Request
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
